import React, { useEffect } from "react";
import { Container, Grid } from "@mui/material";
import { Form } from "react-final-form";
import { useSelector, useDispatch } from "react-redux";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { ProgressBar } from "../../../_uiComponents/Onboarding/ProgressBar";
import { CloseIcon } from "../../../../styles/img/svg/CloseIcon";
import { BackIcon } from "../../../../styles/img/svg/BackIcon";
import { removeFormDataFeilds } from "../../../../_redux/_actions/onboarding.actions";
import { clearCommonErrors } from "../../../../_redux/_actions";
import Validator from "../../../../_helpers/validate.schema";
import UiInput, {
  UiInputPasswordWithFinalForm,
} from "../../../_uiComponents/UiInput";
import {
  passwordValidatePolicy,
  specialCharactersCheck,
} from "../../../../_helpers/support.services";
import { UiButton } from "../../../_uiComponents/common/UiButton";
import { onboardingConstants } from "../../../../_redux/_constants";
import { UiValidationMessage } from "../../../_uiComponents/common/UiValidationMessage";

export const NewPasswordForm = (props) => {
  const { ownFormData: ownFormData } = useSelector((state) => state.onboarding);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const goBack = () => {
    dispatch(clearCommonErrors());
    props?.changeFlowStep(onboardingConstants.ONBOARDING_USERNAME_STEP);
  };

  const validateSchema = () =>
    yup.object().shape({
      password: yup
        .string()
        .matches(
          /^[A-Za-z\d!#$%&*()@^+=]+$/,
          t("Some special characters are not allowed."),
        )
        .required(t("This field is required")),
      confirmPassword: yup.string().when("password", {
        is: (val) => passwordValidatePolicy(val),
        then: (schema) => schema.required(t("This field is required")),
        otherwise: (schema) => schema.notRequired(),
      }),
    });

  const onValidate = async (values) => {
    return await Validator(values, validateSchema);
  };

  const submit = async (values) => {
    // Keep password in ephemeral wrapper state only — never Redux.
    props?.setEphemeralPassword?.(values.password);

    dispatch(removeFormDataFeilds(["nickname"]));

    props?.changeFlowStep(onboardingConstants.ONBOARDING_DISPLAY_NAME_STEP);
  };

  const ephemeralPassword = props?.getEphemeralPassword?.() || "";

  return (
    <>
      <div className="df-user-profile df-reset-cd-form df-onboard-progress-back-close df-profile-top-aligned-box">
        <Container maxWidth="md" className="df-pos-rel">
          <div
            className="df-user-profile__close-icon"
            onClick={props?.closeBtn}
          >
            <CloseIcon />
          </div>

          <div className="df-user-profile__back-icon df-bc-w-progress">
            <ProgressBar
              noOfActive={5}
              total={
                ownFormData?.registrationType ==
                onboardingConstants.REGISTRATION_OPTION_CASA_ACCOUNT
                  ? 7
                  : 6
              }
            />

            <BackIcon onClick={goBack} />
          </div>

          <div className="df-user-profile__body df-pw-tab df-card-details-tb">
            <Grid container>
              <Grid size={12} className="text-left df-pl-2">
                <h2 className="df-reset-pw-title">
                  {t("Enter a password for your digital banking account")}
                </h2>

                <Form
                  onSubmit={submit}
                  validate={onValidate}
                  initialValues={{
                    password: ephemeralPassword,
                    confirmPassword: ephemeralPassword,
                  }}
                  render={({
                    handleSubmit,
                    values,
                    hasValidationErrors,
                    form,
                  }) => {
                    const { dirty } = form.getState();
                    const isPasswordValid = passwordValidatePolicy(values.password);

                    React.useEffect(() => {
                      if (!isPasswordValid && values.confirmPassword) {
                        form.change("confirmPassword", "");
                      }
                    }, [isPasswordValid, values.confirmPassword, form]);

                    return (
                      <>
                        <form onSubmit={handleSubmit}>
                          <UiInputPasswordWithFinalForm
                            name="password"
                            id="setting-change-password-field"
                            label={t("Password")}
                            type="password"
                            minLength="8"
                            maxLength="32"
                            varient="outlined"
                            passwordValue={values?.password}
                            required
                            passwordValidatePolicy={passwordValidatePolicy}
                            specialCharactersCheck={specialCharactersCheck}
                            enableStrength
                            enableConditions
                            disableCopyPaste
                          />
                          <div
                            className={
                              !isPasswordValid && "df-reset-pw-form"
                            }
                          >
                            <UiInput
                              name="confirmPassword"
                              value={values.confirmPassword}
                              id="setting-change-password-confirm-password-field"
                              label={t("Confirm Password")}
                              type="password"
                              maxLength="32"
                              disabled={!isPasswordValid}
                              varient="outlined"
                              required
                              onCopy={(e) => e.preventDefault()}
                              onPaste={(e) => e.preventDefault()}
                              onCut={(e) => e.preventDefault()}
                            />
                          </div>
                          {isPasswordValid &&
                            values.confirmPassword &&
                            values.confirmPassword !== values.password && (
                              <UiValidationMessage
                                message={t("Both passwords do not match.")}
                              />
                            )}
                          <UiButton
                            className="df-ui-dark-bg-btn w-100 mt-2"
                            name={t("Next")}
                            type="button"
                            disabled={
                              hasValidationErrors ||
                              !isPasswordValid ||
                              values.confirmPassword !== values.password
                            }
                            onClick={handleSubmit}
                          />
                        </form>
                      </>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    </>
  );
};
